import {LocalStorage} from '../../utils/LocalStorage';
import {STORAGE_VERSION} from './wizardReducer';
import {clearWizardMemory} from './wizardMemory';

/**
 * Lo que el usuario decidió, guardado para que sobreviva a una recarga, a la vuelta del OAuth y al
 * día siguiente.
 *
 * Se guardan SÓLO las decisiones (qué cuentas entran, de qué cliente son, cuánto presupuesto), no
 * los datos del servidor: esos se vuelven a pedir siempre. La distinción importa — lo guardado es
 * una intención, que sigue siendo válida aunque el mundo cambie; una copia de los datos sería una
 * verdad vencida.
 *
 * Dos cosas que no son negociables y por eso el I/O va por `LocalStorage` y no por una clave propia:
 * el borrador se guarda **por agencia y por usuario**, y se borra entero al cerrar sesión. Sin lo
 * primero, dos personas del mismo equipo en la misma computadora se pisan el armado; sin lo segundo,
 * quien se desloguea le deja sus decisiones a la cuenta que entre después.
 *
 * El borrador vence: armar la cartera es una sesión de trabajo, y retomar un armado de la semana
 * pasada contra una propuesta que ya cambió confunde más de lo que ayuda.
 */

const MAX_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * «Este armado ya terminó»: mientras esté en `true`, `saveDecisions` no escribe.
 *
 * Existe por el orden en que pasan las cosas al SALIR del túnel. El provider vuelca lo pendiente
 * cuando se desmonta (`wizardContext.js`, `useEffect(() => () => saveDecisions(pending.current), [])`,
 * que está ahí para que tildar algo y apretar «Continuar» en menos de medio segundo no le deje el
 * borrador viejo al paso siguiente). Pero una salida hace `clearDecisions()` y **después** navega:
 * el provider se desmonta a continuación y vuelve a escribir el borrador que se acababa de borrar.
 * Medido en el QA del 23/09: `remove` y, **6 ms después**, `set` con un `savedAt` nuevo — o sea que
 * ninguna de las cuatro salidas del túnel limpiaba nada, y el armado sobrevivía 24 h.
 *
 * Es un módulo y no un estado de React a propósito: el que tiene que callarse es justamente el
 * volcado del desmontaje, que corre cuando el componente ya no existe.
 */
let discarded = false;

function ids() {
    return {agencyId: LocalStorage.fetchAgencyId(), userId: LocalStorage.fetchUserId()};
}

function removeDraft() {
    const {agencyId, userId} = ids();
    if (!agencyId || !userId) return;
    LocalStorage.removeTunnelDraft(agencyId, userId);
}

export function loadDecisions() {
    // Abrir el túnel es empezar un armado: lo que se decida a partir de acá se guarda de nuevo.
    discarded = false;

    const {agencyId, userId} = ids();
    if (!agencyId || !userId) return null;

    const stored = LocalStorage.fetchTunnelDraft(agencyId, userId);
    if (!stored) return null;

    // Una versión distinta se descarta en silencio: es de un túnel que ya no existe, y migrar
    // decisiones viejas cuesta más de lo que vale rehacerlas.
    if (stored.version !== STORAGE_VERSION) return null;
    // `savedAt` tiene que ser un número: sin eso la resta da `NaN`, la comparación es falsa y el
    // borrador no vencería nunca.
    if (typeof stored.savedAt !== 'number' || Date.now() - stored.savedAt > MAX_AGE_MS) {
        // Vencido: se tira lo viejo, pero el armado que ARRANCA ahora sí se guarda. Por eso no pasa
        // por `clearDecisions()`, que además daría por terminada una sesión que recién empieza.
        clearWizardMemory();
        removeDraft();
        return null;
    }
    if (!stored.decisions || typeof stored.decisions !== 'object') return null;

    const restored = {decisions: stored.decisions, budgetEdits: stored.budgetEdits || {},
                      conversionEdits: stored.conversionEdits || {}};
    // El modo de agrupación se guarda con las decisiones porque describe el mismo trabajo. Sin él,
    // al volver el toggle diría «agrupadas» sobre unas filas que están separadas, y apretar el botón
    // que ya se ve activo re-agruparía todo: un control que miente y encima deshace.
    if (stored.grouping) restored.grouping = stored.grouping;
    return restored;
}

export function saveDecisions({decisions, budgetEdits, conversionEdits, grouping}) {
    // El volcado del desmontaje llega DESPUÉS de la salida: ver `discarded`.
    if (discarded) return false;

    const {agencyId, userId} = ids();
    if (!agencyId || !userId) return false;

    return LocalStorage.saveTunnelDraft(agencyId, userId, {
        version: STORAGE_VERSION, savedAt: Date.now(), decisions, budgetEdits, conversionEdits, grouping,
    });
}

export function clearDecisions() {
    // Se marca ANTES de borrar: lo que tiene que quedar callado es lo que venga después, y el
    // desmontaje del provider puede llegar en el mismo tick que la navegación.
    discarded = true;
    // La memoria viva se limpia igual aunque no haya ids: es de esta pestaña y este armado terminó.
    clearWizardMemory();
    removeDraft();
}
